import type { ReactNode } from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { cn } from '~/lib/cn'

/**
 * What a glyph is called, said under it while the pointer rests on it or the keyboard reaches
 * it — the web design spec. A glyph carries its name for ears at all times; this is the same
 * name for eyes, and it floats over the page rather than growing out beside the glyph, so a
 * row of glyphs is the same width whatever the pointer is doing over it.
 */
export function Tooltip({
  label,
  side = 'bottom',
  children,
}: {
  label: string
  side?: 'top' | 'bottom' | 'left' | 'right'
  /** The control it names, which is what the tooltip hangs from. */
  children: ReactNode
}) {
  return (
    <TooltipPrimitive.Provider delayDuration={350} skipDelayDuration={200}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            className={cn(
              'z-50 rounded-lg bg-surface-overlay px-2 py-1 text-xs font-medium text-ink shadow-lg ring-1 ring-white/10',
              'select-none data-[state=delayed-open]:animate-[float-in_130ms_cubic-bezier(0.2,0,0,1)]',
            )}
          >
            {label}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}
